import React, { useState, useEffect } from 'react';
import { Container, CssBaseline, ThemeProvider, createTheme, Typography, Box, Link, Paper, Fade } from '@mui/material';
import EmotionalInput from './components/EmotionalInput';
import VerseDisplay from './components/VerseDisplay';
import { getGeminiResponse } from './services/geminiService';
import { TherapistResponse } from './types/types';
import FacebookIcon from '@mui/icons-material/Facebook';

const theme = createTheme({
  palette: {
    primary: {
      main: '#2E5D4B', // Softer, more muted emerald
      light: '#4A7D6A',
      dark: '#1A3F32',
    },
    secondary: {
      main: '#CBA165', // Muted gold
      light: '#E6C48B',
    },
    background: {
      default: '#FAF9F6', // Very warm off-white (Alabaster)
      paper: '#FFFFFF',
    },
    text: {
      primary: '#37474F', // Softer black
      secondary: '#607D8B',
    },
  },
  typography: {
    fontFamily: '"Noto Sans", "Roboto", "Arial", sans-serif',
    h1: {
      fontFamily: '"Noto Naskh Arabic", "traditional arabic", serif',
    },
    h2: {
      fontFamily: '"Noto Naskh Arabic", "traditional arabic", serif',
    },
    h3: {
      fontFamily: '"Noto Naskh Arabic", "traditional arabic", serif',
    },
    h4: {
      fontFamily: '"Noto Naskh Arabic", "traditional arabic", serif',
    },
    h5: {
      fontFamily: '"Noto Naskh Arabic", "traditional arabic", serif',
    },
    h6: {
      fontFamily: '"Noto Naskh Arabic", "traditional arabic", serif',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'linear-gradient(to bottom right, rgba(255,255,255,0.9), rgba(255,255,255,0.95))',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
          border: '1px solid rgba(255, 255, 255, 0.18)',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          backgroundImage: `
            radial-gradient(circle at 20% 20%, rgba(46, 125, 50, 0.03) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(195, 145, 107, 0.03) 0%, transparent 50%)
          `,
        },
      },
    },
  },
});

function App() {
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<TherapistResponse | null>(null);
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSplash(false);
    }, 4000); // Show splash screen for 2 seconds

    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (input: string) => {
    setIsLoading(true);
    try {
      const result = await getGeminiResponse(input);
      setResponse(result);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (showSplash) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
            color: 'white',
            zIndex: 9999,
          }}
        >
          {/* Animated Background Circles */}
          <Box sx={{
            position: 'absolute',
            top: '20%',
            left: '20%',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(212, 175, 55, 0.2) 0%, transparent 70%)',
            filter: 'blur(40px)',
            animation: 'pulse 4s infinite ease-in-out',
            zIndex: 0,
          }} />
          <Box sx={{
            position: 'absolute',
            bottom: '20%',
            right: '20%',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
            filter: 'blur(60px)',
            animation: 'pulse 6s infinite ease-in-out reverse',
            zIndex: 0,
          }} />

          <Fade in={showSplash} timeout={1000}>
            <Box
              textAlign="center"
              p={4}
              sx={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Box
                component="img"
                src="/profile.png"
                alt="Mohammad Fahil"
                sx={{
                  width: { xs: 150, md: 180 },
                  height: { xs: 150, md: 180 },
                  borderRadius: '50%',
                  border: '4px solid rgba(255, 255, 255, 0.9)',
                  boxShadow: '0 0 40px rgba(212, 175, 55, 0.6)',
                  mb: 5,
                  objectFit: 'cover',
                  animation: 'float 3s ease-in-out infinite',
                }}
              />

              <Typography
                variant="h1"
                sx={{
                  fontFamily: '"Amiri", "Noto Naskh Arabic", serif',
                  fontSize: { xs: '2.8rem', sm: '3.5rem', md: '5rem' },
                  fontWeight: 700,
                  mb: 1,
                  mt: 2,
                  background: `linear-gradient(to right, #FFFFFF, ${theme.palette.secondary.light}, #FFFFFF)`,
                  backgroundSize: '200% auto',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  animation: 'shine 3s linear infinite',
                  textShadow: '0 10px 30px rgba(0,0,0,0.3)',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.4,
                  px: 2,
                }}
              >
                القرآن المرشد
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  fontFamily: '"Amiri", serif',
                  opacity: 0.95,
                  fontWeight: 400,
                  fontSize: { xs: '1.4rem', md: '1.8rem' },
                  mt: 2,
                  animation: 'fadeInUp 1s ease-out 0.5s both',
                  textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                  letterSpacing: '0.02em',
                }}
              >
                رفيقك الروحي للسكينة والطمأنينة
              </Typography>

              <Box
                sx={{
                  width: '60px',
                  height: '4px',
                  background: theme.palette.secondary.main,
                  mt: 5,
                  mb: 5,
                  borderRadius: '2px',
                  animation: 'expandWidth 1.5s ease-out forwards',
                  boxShadow: '0 0 15px rgba(212, 175, 55, 0.8)',
                }}
              />

              <Typography
                variant="h6"
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: { xs: '0.9rem', md: '1rem' },
                  opacity: 0.9,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  animation: 'fadeIn 1.5s ease-out 1s both',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                }}
              >
                Developed by Mohammad Fadel Fahil
              </Typography>
            </Box>
          </Fade>
        </Box>
        <style>{`
          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-15px); }
            100% { transform: translateY(0px); }
          }
           @keyframes pulse {
            0% { transform: scale(1); opacity: 0.6; }
            50% { transform: scale(1.1); opacity: 0.8; }
            100% { transform: scale(1); opacity: 0.6; }
          }
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
           @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes shine {
            to {
              background-position: 200% center;
            }
          }
          @keyframes expandWidth {
            from { width: 0; opacity: 0; }
            to { width: 80px; opacity: 1; }
          }
        `}</style>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'inherit',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(255, 255, 255, 0.4)',
            zIndex: 0,
          },
          background: `url('/background.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          pt: 4,
          pb: 6,
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            textAlign="center"
            mb={6}
            dir="rtl"
            sx={{
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '-20px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '200px',
                height: '2px',
                background: `linear-gradient(90deg, 
                  transparent 0%, 
                  ${theme.palette.primary.main} 50%, 
                  transparent 100%
                )`,
              }
            }}
          >
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontFamily: '"Noto Naskh Arabic", "traditional arabic", serif',
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                color: theme.palette.primary.main,
                mb: 2,
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                letterSpacing: '0.02em'
              }}
            >
              القرآن المرشد

            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontFamily: '"Noto Naskh Arabic", "traditional arabic", serif',
                fontSize: { xs: '1.2rem', md: '1.5rem' },
                color: theme.palette.text.secondary,
                fontWeight: 500,
                maxWidth: '600px',
                mx: 'auto',
                lineHeight: 1.8
              }}
            >
              اكتشف السكينة والطمأنينة في آيات القرآن الكريم
            </Typography>
          </Box>

          <Box flex={1}>
            <EmotionalInput onSubmit={handleSubmit} isLoading={isLoading} />
            {response && <VerseDisplay response={response} />}
          </Box>

          <Paper
            component="footer"
            elevation={0}
            sx={{
              mt: 6,
              py: 3,
              px: 2,
              background: 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(10px)',
              borderRadius: 2,
              textAlign: 'center'
            }}
          >
            <Typography
              variant="body2"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                fontFamily: '"Noto Sans", "Roboto", sans-serif',
                fontSize: '0.9rem',
                color: theme.palette.text.secondary,
                '& a': {
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    color: theme.palette.primary.main,
                  }
                }
              }}
            >
              Developed by Mohammad Fahil
              <Link
                href="https://www.facebook.com/profile.php?id=100009652307408"
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  ml: 1,
                  '&:hover': {
                    transform: 'scale(1.1)',
                  }
                }}
              >
                <FacebookIcon sx={{ fontSize: 20 }} />
              </Link>
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 1,
                fontFamily: '"Noto Sans", "Roboto", sans-serif',
                fontSize: '0.8rem',
                color: theme.palette.text.secondary,
                opacity: 0.8,
              }}
            >
              تاريخ الإنتاج: 12/04/2025
            </Typography>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default App;
